import React, { useState } from 'react';
import useCanonical from 'hooks/useCanonical';

// ─── Competitor comparison data ──────────────────────────────────────────────
// Shown in the "Compare us vs. the competition" view. Company names and
// positioning are used under nominative fair use. Drop an SVG logo at
// public/pricing/logos/<slug>.svg to replace the text-chip fallback.
const COMPETITORS = [
  { slug: 'ehandshake',   name: 'eHandshake',    logo: '/images/ehandshake-logo.svg', isOurs: true },
  { slug: 'buffer',       name: 'Buffer',        logo: '/pricing/logos/buffer.svg' },
  { slug: 'hootsuite',    name: 'Hootsuite',     logo: '/pricing/logos/hootsuite.svg' },
  { slug: 'later',        name: 'Later',         logo: '/pricing/logos/later.svg', logoClass: 'h-[22px] md:h-[26px]' },
  { slug: 'socialbee',    name: 'SocialBee',     logo: '/pricing/logos/socialbee.svg' },
  { slug: 'sproutsocial', name: 'Sprout Social', logo: '/pricing/logos/sproutsocial.svg' },
];

// Each row: label + per-competitor cell. Cell values:
//   true  = ✓, false = ✗, string = short text (e.g. "$6/mo")
// First row is the team-amplification differentiator — rendered with a
// highlighted background so the gap jumps out.
const COMPARISON_ROWS = [
  {
    label: 'Team cross-promotion',
    tooltip: 'Every teammate\u2019s audience amplifies your posts. Your reach compounds with every new teammate who joins.',
    isSignature: true,
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Built for local small businesses',
    cells: { ehandshake: true, buffer: 'Generic', hootsuite: 'Enterprise', later: 'Creators', socialbee: true, sproutsocial: 'Enterprise' },
  },
  {
    label: 'Publish to Facebook / Instagram / X / LinkedIn / TikTok / YouTube',
    cells: { ehandshake: true, buffer: true, hootsuite: true, later: true, socialbee: true, sproutsocial: true },
  },
  {
    label: 'AI-generated headlines, captions, hashtags, emoji',
    cells: { ehandshake: true, buffer: 'Limited', hootsuite: true, later: true, socialbee: true, sproutsocial: true },
  },
  {
    label: 'Profession-targeted post ideas — fresh, ready to use',
    tooltip: 'Open the Design Center and see a row of post ideas written for your trade. No prompting, no googling, no "what do I say?" Tap "Use This Idea" and the post is half-built.',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Crowd-sourced ideas from others in your profession',
    tooltip: 'See post ideas other businesses in your profession are actually using — community-tuned, not just AI-generic.',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Built-in stock image & video library',
    cells: { ehandshake: '3.2M assets', buffer: false, hootsuite: 'Limited', later: 'Unsplash', socialbee: 'Canva', sproutsocial: 'Limited' },
  },
  {
    label: 'Auto-convert still images to Reels / TikTok / Shorts video',
    tooltip: 'Turn any photo into a short Ken Burns video tuned for short-form platforms. No editor, no separate tool.',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Royalty-free background music for video posts',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Built-in landing-page builder',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: 'Link-in-bio only', socialbee: false, sproutsocial: false },
  },
  {
    label: 'AI-generated landing-page copy + sections',
    tooltip: 'Every post links to its own landing page — AI writes the headline, body copy, sections, and CTA. Or use your own URL instead.',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Per-platform post preview before publish',
    cells: { ehandshake: true, buffer: true, hootsuite: true, later: true, socialbee: true, sproutsocial: true },
  },
  {
    label: 'Per-platform engagement analytics in one view',
    cells: { ehandshake: true, buffer: true, hootsuite: true, later: true, socialbee: true, sproutsocial: true },
  },
  {
    label: 'Email any post to your customer list',
    tooltip: 'Send a creation, a teammate’s profile, a company, or your team card straight to customers from your CRM. No separate email tool.',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Branded customer review-request emails',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Public profile with customer reviews & star ratings',
    tooltip: 'Customers leave reviews on your eHandshake profile; ratings aggregate to a public page you can link from anywhere.',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Recommend teammates, companies, or whole teams',
    tooltip: 'Send a teammate’s profile card, their company card, or your entire team to your audience as a single shareable post.',
    cells: { ehandshake: true, buffer: false, hootsuite: false, later: false, socialbee: false, sproutsocial: false },
  },
  {
    label: 'Free tier (not a trial)',
    cells: { ehandshake: true, buffer: true, hootsuite: false, later: true, socialbee: false, sproutsocial: false },
  },
  {
    // Our cell shows 'Coming Soon' while prices are hidden 2026-05-30.
    // Competitor cells stay — that's public industry data, not our price.
    // Restore 'ehandshake: $19.99/mo' when prices go live.
    label: 'Starting paid price',
    cells: { ehandshake: 'Coming Soon', buffer: '$6/ch/mo (12 ch = $72)', hootsuite: '$99/mo', later: '$25/mo', socialbee: '$29/mo', sproutsocial: '$249/mo' },
  },
];

// Cell renderer — turns true/false/string into visual content.
function ComparisonCell({ value, isOurs, isSignature }) {
  if (value === true) {
    return (
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${isSignature && isOurs ? 'bg-white text-[#00aef7]' : isOurs ? 'bg-[#00aef7] text-white' : 'bg-green-100 text-green-700'} text-sm font-bold`}>
        ✓
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-50 text-red-500 text-sm font-bold">
        ✗
      </span>
    );
  }
  // String value
  return (
    <span className={`text-xs md:text-sm ${isOurs ? 'font-semibold' : 'text-gray-700'}`}>
      {value}
    </span>
  );
}

// The comparison table itself — rendered inside the same container that
// holds the pricing tier cards, so a user toggles between the two views
// without leaving the page.
function ComparisonTable() {
  return (
    <div className="mt-2">
      <div className="bg-white rounded-[32px] shadow-[0_10px_30px_rgba(0,0,0,0.12)] overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 text-[11px] font-bold uppercase tracking-widest text-gray-500">
                  Feature
                </th>
                {COMPETITORS.map((c) => (
                  <th
                    key={c.slug}
                    className={`px-1.5 md:px-2 py-3 text-center align-bottom ${c.isOurs ? 'bg-[#00aef7]/10' : ''}`}
                  >
                    <div className="flex flex-col items-center gap-1.5">
                      {c.logo && (
                        <img
                          src={c.logo}
                          alt={`${c.name} logo`}
                          className={`${c.logoClass || 'h-7 md:h-8'} w-auto object-contain`}
                          loading="lazy"
                        />
                      )}
                      <div className={`whitespace-nowrap text-xs md:text-sm font-bold ${c.isOurs ? 'text-[#00aef7]' : 'text-gray-900'}`}>
                        {c.name}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr
                  key={i}
                  className={`border-b border-gray-100 last:border-b-0 ${
                    row.isSignature ? 'bg-[#00aef7]/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <td className={`px-4 py-3 text-xs md:text-sm ${row.isSignature ? 'font-bold text-gray-900' : 'text-gray-800'}`} title={row.tooltip || undefined}>
                    {row.label}
                    {row.isSignature && (
                      <span className="ml-2 inline-block text-[9px] font-bold uppercase tracking-widest text-[#00aef7] bg-white border border-[#00aef7]/30 rounded-full px-2 py-0.5 align-middle">
                        Only on eHandshake
                      </span>
                    )}
                  </td>
                  {COMPETITORS.map((c) => {
                    const value = row.cells[c.slug];
                    const bg = row.isSignature && c.isOurs
                      ? 'bg-[#00aef7] text-white'
                      : c.isOurs
                        ? 'bg-[#00aef7]/10'
                        : '';
                    return (
                      <td key={c.slug} className={`px-3 py-3 text-center ${bg}`}>
                        <ComparisonCell value={value} isOurs={c.isOurs} isSignature={row.isSignature} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-3 text-center text-[10px] text-gray-400">
        Competitor names and marks belong to their respective owners and are shown here under nominative fair use for comparison.
      </p>
    </div>
  );
}

const tiers = [
  {
    key: 'practice',
    label: 'Free Agent',
    accent: 'border-[#00aef7]',
    image: 'Whistle.png',
    imageSize: 'w-12 h-12',
    monthly: 0,
    annual: 0,
    features: [
      'Design 1 business creation for sharing to Social Media or by email, per month',
      'Share other users business creations to your channels and by email',
      'Other users can share your business creations to their channels and by email',
      'Unlimited sharing of others business creations to your channels',
      'Research teams and discover potential teammates',
      'Meet your local business neighbors',
      'Share to your personal social channels',
    ],
  },
  {
    key: 'first',
    label: 'First String',
    accent: 'border-[#44d0b7]',
    image: 'Foam_Finger.png',
    imageSize: 'w-12 h-12',
    monthly: 19.99,
    annual: 215.89,
    features: [
      'Design 5 business creations for sharing to Social Media or by email, per month',
      'Unlimited Trusted Review requests',
      'Respond publicly to customer reviews',
      'Design up to 10 Social Media posts per month',
      'Share to all connected social platforms',
      'Access community events and local networking',
      'Priority placement in team search results',
      'Basic analytics on your review performance',
    ],
  },
  {
    key: 'allstar',
    label: 'All-Star',
    accent: 'border-amber-300',
    image: 'gold_star.png',
    imageSize: 'w-12 h-12',
    monthly: 49.99,
    annual: 539.89,
    features: [
      'Everything in First String',
      'Unlimited Social Media post designs',
      'Schedule and auto-publish to all platforms',
      'Advanced reputation analytics and insights',
      'Showcase your best reviews on your profile',
      'Join and create community groups',
      'Enhanced visibility in local business directory',
      'Custom branding on shared content',
    ],
  },
  {
    key: 'captain',
    label: 'Team Captain',
    accent: 'border-red-300',
    image: 'Captain.png',
    imageSize: 'w-12 h-12',
    monthly: 0,
    annual: 0,
    isCaptain: true,
    features: [
      'Build a team and earn $5/mo credit per member',
      'Everything in First String included',
      'Recruit and manage your team',
      '4 members = your subscription is free',
      'Team performance dashboards',
      'Onboard new members with guided setup',
      'Community leadership recognition badge',
      'Shape the platform as a founding captain',
    ],
  },
];

export default function PricingPage() {
  const [billing, setBilling] = useState('monthly');
  const [view, setView] = useState('pricing'); // 'pricing' | 'compare'

  // Self-referential canonical so Search Console indexes /pricing as its own
  // page instead of folding it into the homepage. The CRA shell's static
  // canonical (public/index.html) points at "/", which was reporting /pricing
  // under "Alternate page with proper canonical tag". See hooks/useCanonical.js.
  useCanonical('https://ehandshake.com/pricing');

  // Pricing temporarily hidden 2026-05-30 — every tier shows "Coming Soon"
  // until Brett finalizes the public price points. Restore the original
  // formatPrice/priceLabel logic (see git history pre-this-commit) when
  // the real prices go live. The tier objects' monthly/annual values are
  // intentionally left intact so the swap-back is one block of code.
  const formatPrice = (_tier) => 'Coming Soon';
  const priceLabel = (_tier) => '';

  return (
    <div className="min-h-screen bg-white">
      <div className="pt-[5px] pb-2">
        <div className="mx-auto w-[90%]">
          {/* Top bar: left view-toggle link + centered billing toggle
              (billing toggle is pricing-view only) */}
          <div className="relative flex justify-center items-center min-h-[44px]">
            <button
              type="button"
              onClick={() => setView(view === 'pricing' ? 'compare' : 'pricing')}
              className="absolute left-0 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#00aef7] hover:text-[#0098d8] underline underline-offset-2"
            >
              {view === 'pricing' ? 'Compare us vs. the competition →' : '← Back to pricing'}
            </button>

            {view === 'pricing' && (
              <div className="inline-flex items-center rounded-full border border-gray-200 bg-white shadow-sm p-1">
                <button
                  type="button"
                  onClick={() => setBilling('monthly')}
                  className={[
                    'px-6 py-2 rounded-full text-sm font-semibold transition-colors',
                    billing === 'monthly'
                      ? 'bg-brand text-white'
                      : 'text-gray-700 hover:text-brand',
                  ].join(' ')}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBilling('annual')}
                  className={[
                    'px-6 py-2 rounded-full text-sm font-semibold transition-colors',
                    billing === 'annual'
                      ? 'bg-brand text-white'
                      : 'text-gray-700 hover:text-brand',
                  ].join(' ')}
                >
                  Annual
                </button>
              </div>
            )}
          </div>

          {view === 'compare' && <ComparisonTable />}

          {/* Cards row */}
          {view === 'pricing' && (
          <div className="mt-2">
            <div className="flex flex-col gap-4 md:flex-row md:gap-3 md:justify-between">
              {tiers.map((t) => (
                <div key={t.key} className="w-full md:flex-1 md:min-w-0">
                  <div
                    className={[
                      'relative bg-white rounded-[32px]',
                      'border-4',
                      t.accent,
                      'shadow-[0_10px_30px_rgba(0,0,0,0.12)]',
                      'overflow-hidden',
                      'md:h-[450px]',
                      'flex flex-col',
                    ].join(' ')}
                  >
                    {/* header */}
                    <div className="px-4 pt-1 flex flex-col items-center shrink-0">
                      <img
                        src={`${process.env.PUBLIC_URL}/pricing/${t.image}`}
                        alt={t.label}
                        className={`${t.imageSize} object-contain`}
                        draggable="false"
                      />
                      <div className="mt-2 text-xl font-bold text-gray-900 text-center whitespace-nowrap">
                        {t.label}
                      </div>

                      {/* price */}
                      <div className="mt-1 flex items-baseline gap-1">
                        <span className="text-2xl font-extrabold text-gray-900">
                          {formatPrice(t)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {priceLabel(t)}
                        </span>
                      </div>
                    </div>

                    {/* divider */}
                    <div className="mx-4 mt-2 border-t border-gray-200" />

                    {/* feature list */}
                    <div className="px-4 pt-2 pb-4 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                      <ul className="space-y-1.5">
                        {t.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                            <span className="mt-0.5 shrink-0 text-green-500">✓</span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
